// web/src/utils/labels/assist-ai-intent.ts —— 坐席辅助 / AI 阶段 / 意图与承诺 / 收号 / 流程轨迹 / 场景色与质检场景
// 拆自 web/src/utils/labels.ts 的同名分区（内容原样搬运）；由 web/src/utils/labels.ts 以 export * 聚合，导出面对调用方不变。

import { maskPhone } from '../format';
import type { PillColor } from '../../components/PillTag';

import { STATUS_LABELS, builtinLabel, isDialplanTarget, statusLabel } from './common';
import { SATISFACTION_SCENE_LABELS, flowNodeLabel } from './flow-ivr-satisfaction';

/**
 * 坐席辅助转写说话人（assist_transcript / ai_transcript 的 speaker）→ 中文。
 * 与在线会话的发送方标识（statusLabel 的 agent/robot）不同口径：通话转写固定三角色，
 * 未知值兜底「客户」（与工作台通话记录原行为一致）。
 */
export const ASSIST_SPEAKER_LABELS: Record<string, string> = {
  customer: '客户',
  agent: '坐席',
  robot: 'AI',
};

export function assistSpeakerLabel(v?: string): string {
  return ASSIST_SPEAKER_LABELS[v || ''] || '客户';
}

/** AI 通话阶段（ai_stage.stage）→ 中文；idle 无文案（不占胶囊位） */
export const AI_STAGE_LABELS: Record<string, string> = {
  asr: '识别中…',
  llm: 'LLM 思考中…',
  tts: '播报中…',
  idle: '',
};

/** 意图绑定动作（ai_intent_vocab.action / assist_intent.action_type）→ 中文 */
export const INTENT_ACTION_LABELS: Record<string, string> = {
  transfer: '转人工',
  goodbye: '结束通话',
  create_ticket: '建工单',
  inject: '注入上下文',
  notify: '升级通知',
  fixed_script: '建议话术',
  ignore: '忽略（静默）',
  llm: 'LLM 对话',
};

export function intentActionLabel(v?: string): string {
  return v ? INTENT_ACTION_LABELS[v] || v : '-';
}

/** 下一步动作推荐（assist_next_actions.actions[].type）→ 中文 */
export const NEXT_ACTION_LABELS: Record<string, string> = {
  confirm_solve: '确认已解决',
  satisfaction: '推送满意度',
  create_ticket: '建单',
  transfer: '转接',
};

export function nextActionLabel(v?: string): string {
  return v ? NEXT_ACTION_LABELS[v] || v : '-';
}

/** 会话控制意图码 → 中文兜底（意图词表未配置 name 时仍可读） */
export const CONTROL_INTENT_LABELS: Record<string, string> = {
  greeting: '问候',
  goodbye: '结束通话',
  transfer_human: '转人工',
  cancel_request: '放弃',
  ignore: '忽略',
};

export function controlIntentLabel(v?: string): string {
  return v ? CONTROL_INTENT_LABELS[v] || v : '-';
}

/** 承诺度关系（assist_commitment.relation）→ [文案, 语义色] */
export const COMMITMENT_RELATION_LABELS: Record<string, [string, PillColor]> = {
  firm_reject: ['明确拒绝', 'red'],
  soft_reject: ['温和拒绝', 'red'],
  objection: ['异议/顾虑', 'orange'],
  conditional_accept: ['有条件接受', 'orange'],
  unclear: ['模糊', 'gray'],
  accept: ['接受', 'green'],
  enthusiastic_accept: ['热烈接受', 'green'],
  question: ['中立追问', 'blue'],
  new_topic: ['转话题', 'gray'],
};

export function commitmentRelationMeta(v?: string): [string, PillColor] {
  return COMMITMENT_RELATION_LABELS[v || ''] || [v || '-', 'gray'];
}

/** AI 采集/建单字段状态（collected_field.status）→ [文案, 语义色] */
export const COLLECT_FIELD_STATUS_LABELS: Record<string, [string, PillColor]> = {
  raw: ['AI 提取', 'blue'],
  confirmed: ['已确认', 'green'],
  revised: ['已修改', 'orange'],
  completed: ['已补充', 'green'],
};

export function collectFieldStatusMeta(v?: string): [string, PillColor] {
  return COLLECT_FIELD_STATUS_LABELS[v || ''] || [v || '-', 'gray'];
}

/** 号码展示统一出口：内置 dialplan 目标 → 中文；否则按手机号脱敏。空值原样返回。 */
export function displayNumber(v?: string): string {
  if (!v) return v ?? '';
  if (isDialplanTarget(v)) return builtinLabel(v);
  return maskPhone(v);
}

/** 流程轨迹（chat trail）事件标签：会话记录「流程轨迹」/ 会话详情 / 工作台实时踪迹共用 */
export const FLOW_TRAIL_EVENT_LABELS: Record<string, string> = {
  node_enter: '进入流程',
  ask: '等待输入',
  say: '话术播报',
  transfer: '转人工',
  handoff: '挂起交接',
  done: '流程完成',
  abort: '流程放弃',
  query_datasource: 'BOSS 查询',
  verify: '身份核验',
  identity: '身份预采集',
  create_ticket: '创建工单',
  notify: '通知',
  subprocess: '子流程',
};

/** 流程轨迹节点 key 中文兜底（biz_flow_node.name 为空时用；自定义中文名优先）。
 * 同时是「流程节点显示名」唯一值表（坐席流程面板、设计器节点、流程轨迹共用，出口 flowNodeLabel） */
export const FLOW_TRAIL_NODE_LABELS: Record<string, string> = {
  start: '开始',
  end: '结束',
  ask_slot: '信息采集',
  ask_confirm: '确认核实',
  read_verify: '身份核验',
  verify: '身份核验',
  query_datasource: '数据查询',
  query: '数据查询',
  say: '话术播报',
  transfer: '转人工',
  intent_switch: '意图分流',
  subprocess: '子流程',
  create_ticket: '创建工单',
  notify: '通知',
  webhook: '接口回调',
  sms: '短信下发',
  play_menu: '菜单选择',
  free_chat: '自由对话',
  condition: '条件判断',
  wait: '等待',
  ask_phone: '手机号',
  ask_kind: '查询类型',
  ask_id: '证件尾号',
  ask_id_tail: '证件尾号',
  ask_name: '客户姓名',
  ask_accept: '确认办理',
  confirm: '确认核实',
  say_result: '播报结果',
  say_no: '拒绝应答',
  say_yes: '确认应答',
  check: '核验检查',
  query_balance: '余额查询',
  query_bills: '账单查询',
  query_realtime: '实时查询',
};

/* 话术/知识建议「场景」call|chat|both：全站共享（scripts 话术库 & 工作台知识面板共用；
 * 值=短枚举 Tag/Pill 色键（arcoblue/cyan/gray），页面消费时按自身体系映射渲染） */
export const SCENE_LABEL: Record<string, string> = { call: '语音', chat: '在线', both: '通用' };
export const SCENE_COLOR: Record<string, string> = { call: 'arcoblue', chat: 'cyan', both: 'gray' };

/* ============================================================
 * 页面内枚举上收登记
 *
 * 各页面/组件原先自建的「后端枚举值 → 中文」映射表统一登记在此，页面只保留下拉/筛选项的
 * options 数组（label/value）并引用本层出口函数，禁止再出现页面级 XXX_LABELS。
 * 口径约定：
 * - 同一枚举已在别处登记（本文件其它表 / utils 或 api 层导出）时，页面直接引用既有出口，
 *   本层不重复登记，避免第三事实源；
 * - 同值不同词的重复表按「以本文件既有口径为准」收口，分歧在表注释写明取值依据；
 * - 出口函数一律：未知值原样返回、空值返回 '-'（meta 版为空/未知回落 gray）。
 * ============================================================ */

/* ---------- 质检域 ---------- */

/**
 * 质检规则类型语义出口（quality_rule.type）。
 * 值表复用 STATUS_LABELS（keyword/forbidden/silence/llm/overlap/consistency/answer/emotion/process_*），
 * 不再另起第二张规则类型表。
 * 流程类统一口径：流程驻留 / 流程覆盖 / 流程关键词 / 流程 LLM——规则模板页原
 * 「环节停留超时 / 环节覆盖缺失 / 处理结果关键词 / 处理结果 LLM」按此收口，避免同值两词。
 */
export function qualityRuleTypeLabel(v?: string): string {
  return statusLabel(v);
}

/**
 * 质检场景（quality_report.scene / quality_job_item.scene / quality_sample.scene）→ 中文。
 * 与 SCENE_LABEL（话术/知识场景：语音/在线/通用）、SATISFACTION_SCENE_LABELS（满意度场景）区分，
 * 不复用、不合并；未知值原样返回。
 */
export const QUALITY_SCENE_LABELS: Record<string, string> = {
  call: '通话质检',
  chat: '在线质检',
  flow: '流程质检',
};

export function qualitySceneLabel(v?: string): string {
  if (!v) return '-';
  return QUALITY_SCENE_LABELS[v] || v;
}

